import { Component, Input, Output, EventEmitter, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Schedule } from 'src/providers/schedule/schedule';
import { ReportIssue } from 'src/providers/report-issue/report-issue';
import { Util } from 'src/shared/util/util';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import {
  RestockingOrdersService,
  RestockOrder,
  AssignedBooking,
  isRestockJobAssignable
} from 'src/shared/services/restocking-orders.service';

/**
 * Self-contained "Assign to Job" control for a restock order: attached-job chips
 * plus a searchable dropdown of the address's assignable jobs. Used by the
 * Restocking page card and the order detail page so both share one picker
 * (styles live in global.scss under `.restock-assign-job` and the shared
 * `.restock-job-*` classes). See card gCUZuCjf.
 */

interface JobPickerItem {
  bookingId: number;
  label: string;
  searchText: string;
  statusLabel: string;
  proInitials: string;
  icon: string;
}

interface AssignedChip {
  bookingId: number;
  label: string;
  icon: string;
  proInitials: string;
  removable: boolean;   // only pending attempts can be detached
}

@Component({
  selector: 'restock-assign-job',
  templateUrl: './restock-assign-job.component.html',
  styleUrls: ['./restock-assign-job.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, TidyUiComponentsModule, SharedModule]
})
export class RestockAssignJobComponent implements OnChanges {
  @Input() order!: RestockOrder;
  /** Emitted after an attach/detach so a parent can refresh other views. */
  @Output() changed = new EventEmitter<void>();

  canAssignJob = false;
  assignedChips: AssignedChip[] = [];
  showJobSelect = false;
  jobItems: JobPickerItem[] = [];
  filteredJobItems: JobPickerItem[] = [];
  jobSearch = '';
  jobItemsLoaded = false;

  private jobMetaByBookingId = new Map<number, { icon: string; proInitials: string }>();
  private jobMetaAddressId: number | null = null;

  constructor(
    private schedule: Schedule,
    private reportIssue: ReportIssue,
    private restockingOrders: RestockingOrdersService,
    private util: Util,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(): void {
    this.rebuild();
    this.maybeLoadJobMeta();
  }

  private rebuild(): void {
    const order = this.order;
    this.canAssignJob = order?.state === 'ordered' || order?.state === 'delivered';
    this.assignedChips = (order?.assignedBookings || []).map((booking) => ({
      bookingId: booking.bookingId,
      label: this.buildChipLabel(booking),
      icon: this.jobMetaByBookingId.get(booking.bookingId)?.icon || 'assets/svg/calendar-outline.svg',
      proInitials: this.initialsFrom(booking.proName),
      removable: booking.state === 'pending',
    }));
    this.cdr.markForCheck();
  }

  // Loads the address's jobs once so chips can show a category icon (the order
  // payload's assigned_bookings carry pro name + date, but no service icon).
  // Runs when the order's address first becomes known (the parent may set the
  // order input after init).
  private async maybeLoadJobMeta(): Promise<void> {
    const addressId = this.order?.propertyId;
    if (!addressId || this.jobMetaAddressId === addressId) {
      return;
    }
    this.jobMetaAddressId = addressId;
    try {
      const jobs = await this.schedule.getJobs(addressId);
      (jobs || []).forEach((job) => {
        const bookingId = this.resolveBookingId(job);
        if (!bookingId) {
          return;
        }
        this.jobMetaByBookingId.set(bookingId, {
          icon: job?.service_icon_url || 'assets/svg/calendar-outline.svg',
          proInitials: this.initialsFrom(job?.homekeeper_jobs?.[0]?.homekeeper?.name || job?.potential_homekeeper?.name || ''),
        });
      });
      this.rebuild();
    } catch {
      // best-effort — chips fall back to the default calendar icon
    }
  }

  async toggleJobSelect(event?: Event): Promise<void> {
    event?.stopPropagation();
    if (this.showJobSelect) {
      this.showJobSelect = false;
      return;
    }
    this.showJobSelect = true;
    this.jobSearch = '';
    this.jobItemsLoaded = false;
    this.jobItems = await this.loadJobItems();
    this.filteredJobItems = this.jobItems;
    this.jobItemsLoaded = true;
    this.cdr.markForCheck();
  }

  cancelJobSelect(event?: Event): void {
    event?.stopPropagation();
    this.showJobSelect = false;
  }

  onJobSearch(term: string): void {
    this.jobSearch = term;
    const needle = (term || '').trim().toLowerCase();
    this.filteredJobItems = needle
      ? this.jobItems.filter((item) => item.searchText.includes(needle))
      : this.jobItems;
  }

  async onJobSelected(bookingId: number, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!bookingId) {
      return;
    }
    this.showJobSelect = false;
    try {
      this.order = await this.restockingOrders.assignBooking(this.order.id, bookingId);
      this.rebuild();
      this.changed.emit();
      this.util.showSuccess('Order attached to job.');
    } catch (error) {
      this.util.showError(error?.message || 'Could not attach the order to that job.');
    }
  }

  async unassignJob(bookingId: number, event?: Event): Promise<void> {
    event?.stopPropagation();
    try {
      this.order = await this.restockingOrders.unassignBooking(this.order.id, bookingId);
      this.rebuild();
      this.changed.emit();
    } catch (error) {
      this.util.showError(error?.message || 'Could not remove the assignment.');
    }
  }

  private async loadJobItems(): Promise<JobPickerItem[]> {
    try {
      const assignedBookingIds = new Set(this.assignedChips.map((chip) => chip.bookingId));
      const jobs = await this.schedule.getJobs(this.order.propertyId);
      return (jobs || [])
        .map((job) => ({ job, bookingId: this.resolveBookingId(job) }))
        .filter((entry) => isRestockJobAssignable(entry.job)
          && entry.bookingId
          && !assignedBookingIds.has(entry.bookingId))
        .map(({ job, bookingId }) => this.buildJobPickerItem(job, bookingId));
    } catch {
      return [];
    }
  }

  private buildJobPickerItem(job: any, bookingId: number): JobPickerItem {
    const proName = job?.homekeeper_jobs?.[0]?.homekeeper?.name || job?.potential_homekeeper?.name || '';
    const label = `${job?.date || ''} ${job?.start_time || ''}`.trim() || `Job #${job?.id}`;
    const statusData = this.reportIssue.getJobStatusData(job?.homekeeper_jobs, job?.job_progress, job);
    return {
      bookingId,
      label,
      searchText: `${label} ${proName}`.toLowerCase(),
      statusLabel: this.titleCase(statusData?.name),
      proInitials: this.initialsFrom(proName),
      icon: job?.service_icon_url || 'assets/svg/calendar-outline.svg',
    };
  }

  // "Aug 15" (+ outcome once the job is done). Date-only strings parse as UTC
  // midnight, which shifts a day back in behind-UTC timezones — parse as local.
  private buildChipLabel(booking: AssignedBooking): string {
    let label = this.formatShortDate(booking.date) || `Booking #${booking.bookingId}`;
    if (booking.state === 'restocked') {
      label += ' (Restocked)';
    } else if (booking.state === 'not_restocked') {
      label += ' (Not restocked)';
    }
    return label;
  }

  private formatShortDate(iso: string): string {
    if (!iso) {
      return '';
    }
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const date = dateOnly
      ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
      : new Date(iso);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  private initialsFrom(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '';
    }
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  private titleCase(value: string): string {
    return value ? value.replace(/\b\w/g, (char) => char.toUpperCase()) : '';
  }

  private resolveBookingId(job: any): number | undefined {
    return job?.booking?.id
      || job?.booking_id
      || job?.object?.booking?.id
      || job?.template_data?.booking?.id
      || undefined;
  }
}
